// /app/frontend/src/components/compliance/map/EntityNode.jsx
import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Badge } from '../../ui/badge';
import { 
  Database, Cpu, HardDrive, Globe, Users, Building2, 
  ShieldCheck, AlertTriangle, AlertCircle 
} from 'lucide-react';
import { cn } from '../../../lib/utils';

// Node type configurations
const NODE_TYPES = {
  source: {
    icon: Database,
    borderColor: 'border-l-blue-500',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    label: 'Data Source'
  },
  process: {
    icon: Cpu,
    borderColor: 'border-l-purple-500',
    iconBg: 'bg-purple-500/10',
    iconColor: 'text-purple-400',
    label: 'Processing'
  },
  store: {
    icon: HardDrive,
    borderColor: 'border-l-orange-500',
    iconBg: 'bg-orange-500/10',
    iconColor: 'text-orange-400',
    label: 'Storage'
  },
  transfer: {
    icon: Globe,
    borderColor: 'border-l-rose-500',
    iconBg: 'bg-rose-500/10',
    iconColor: 'text-rose-400',
    label: 'Transfer'
  },
  thirdparty: {
    icon: Building2,
    borderColor: 'border-l-cyan-500',
    iconBg: 'bg-cyan-500/10',
    iconColor: 'text-cyan-400',
    label: 'Third Party'
  },
  subjects: {
    icon: Users,
    borderColor: 'border-l-emerald-500',
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
    label: 'Data Subjects'
  }
};

// Risk level configurations
const RISK_LEVELS = {
  low: {
    icon: ShieldCheck,
    color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    label: 'Low Risk'
  },
  medium: {
    icon: AlertCircle,
    color: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    label: 'Medium Risk'
  },
  high: {
    icon: AlertTriangle,
    color: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    label: 'High Risk'
  }
};

const EntityNode = memo(({ data, selected }) => {
  const nodeType = NODE_TYPES[data.type] || NODE_TYPES.source;
  const riskLevel = RISK_LEVELS[data.risk] || RISK_LEVELS.low;
  const Icon = nodeType.icon;
  const RiskIcon = riskLevel.icon;

  return (
    <div
      data-testid={`node-${data.id}`}
      className={cn(
        "min-w-[200px] max-w-[280px]",
        "backdrop-blur-md bg-slate-900/90 rounded-lg",
        "border border-slate-700/50 border-l-4",
        nodeType.borderColor,
        "transition-all duration-300",
        "hover:border-violet-500/50 hover:shadow-lg hover:shadow-violet-500/20",
        selected && "ring-2 ring-violet-500 shadow-lg shadow-violet-500/30"
      )}
    >
      {/* Target Handle (Left) */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-slate-600 !border-2 !border-slate-800 hover:!bg-violet-500"
      />
      
      {/* Node Content */}
      <div className="p-3">
        {/* Header */}
        <div className="flex items-start gap-3 mb-2">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
            nodeType.iconBg
          )}>
            <Icon className={cn("h-5 w-5", nodeType.iconColor)} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">
              {nodeType.label}
            </p>
            <h4 className="text-sm font-semibold text-white truncate">
              {data.label}
            </h4>
          </div>
        </div>
        
        {/* Description */}
        {data.description && (
          <p className="text-xs text-slate-400 mb-2 line-clamp-2">
            {data.description}
          </p>
        )}
        
        {/* Footer - Risk & Data Categories */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-800">
          <Badge 
            variant="outline" 
            className={cn("text-[10px] px-1.5 py-0 h-5", riskLevel.color)}
          >
            <RiskIcon className="h-3 w-3 mr-1" />
            {riskLevel.label}
          </Badge>
          
          {data.dataCategories && data.dataCategories.length > 0 && (
            <span className="text-[10px] text-slate-500">
              {data.dataCategories.length} data types
            </span>
          )}
        </div>
        
        {/* Country indicator for transfers */}
        {data.type === 'transfer' && data.country && (
          <div className="mt-2 pt-2 border-t border-slate-800 flex items-center gap-2">
            <Globe className="h-3 w-3 text-rose-400" />
            <span className="text-xs text-slate-400">{data.country}</span>
            {data.hasAdequacy && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                Adequacy
              </Badge>
            )}
          </div>
        )}
      </div>
      
      {/* Source Handle (Right) */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-slate-600 !border-2 !border-slate-800 hover:!bg-violet-500"
      />
    </div>
  );
});

EntityNode.displayName = 'EntityNode';

export default EntityNode;
